import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "@/lib/firebase";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  getCountFromServer,
  DocumentData,
} from "firebase/firestore";
import { Card, CardContent } from "@/components/ui/card";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Grid3x3, Sparkles } from "lucide-react";

type CategoryCard = {
  id: string;
  name: string;
  count: number;
  image: string | null;
};

const toTitleCase = (value: string) =>
  value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const toSlug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const ShopByCategory: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<CategoryCard[]>([]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollByAmount = (direction: "left" | "right") => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.6);
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const categoriesCol = collection(db, "categories");
        const snapshot = await getDocs(query(categoriesCol, orderBy("name")));

        const docs = snapshot.docs;
        const results: CategoryCard[] = [];

        // Fetch counts and a sample image per category (efficient small queries per category)
        await Promise.all(
          docs.map(async (doc) => {
            const name: string = (doc.data().name || "").toString();
            if (!name) return;

            const productsCol = collection(db, "products");
            // Count using aggregate (simple equality constraint to avoid composite index requirements)
            const countSnap = await getCountFromServer(
              query(productsCol, where("category", "==", name))
            );
            const count = countSnap.data().count || 0;
            if (count <= 0) return;

            // Grab one image to use as background
            const oneSnap = await getDocs(
              query(
                productsCol,
                where("category", "==", name),
                where("disabled", "==", false),
                orderBy("__name__"),
                limit(1)
              )
            );
            let image: string | null = null;
            if (!oneSnap.empty) {
              const data = oneSnap.docs[0].data() as DocumentData;
              image = (data.images && data.images[0]) || data.image || null;
            }

            results.push({ id: doc.id, name, count, image });
          })
        );

        if (isMounted) {
          setCategories(results);
        }
      } catch (e) {
        console.error("Failed to load categories", e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, []);

  // Attach scroll and resize listeners to manage arrow enable state
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollState();
    const onScroll = () => updateScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    const onResize = () => updateScrollState();
    window.addEventListener("resize", onResize);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [categories.length, updateScrollState]);

  if (loading) {
    return (
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center">
            <LoadingSpinner />
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) return null;

  return (
    <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white py-12 sm:pt-16 overflow-hidden">
      {/* Background visuals to match FeaturedProducts */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(rgba(0, 116, 47, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px), linear-gradient(rgba(255, 201, 1, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)",
              backgroundSize: "60px 60px, 60px 60px, 120px 120px, 120px 120px",
              backgroundPosition: "0 0, 0 0, 30px 30px, 30px 30px",
            }}
          ></div>
        </div>
        <div className="absolute top-20 left-20 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10"></div>
        <div className="absolute top-40 right-32 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15"></div>
        <div className="absolute bottom-32 left-1/3 w-0 h-0 border-l-[25px] border-r-[25px] border-b-[43px] border-l-transparent border-r-transparent border-b-jamaica-green/8"></div>
        <div className="absolute bottom-20 right-20 w-0 h-0 border-l-[18px] border-r-[18px] border-b-[31px] border-l-transparent border-r-transparent border-b-jamaica-yellow/12"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header matching other sections */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
            <div className="relative">
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                <Grid3x3 className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/30 shadow-lg">
              <span className="text-xs sm:text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-jamaica-green" />
                Browse Categories
              </span>
            </div>
          </div>

          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
              Shop by Category
            </span>
          </h2>
          <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover curated collections of 
            <span className="text-jamaica-green font-semibold"> authentic Jamaican products</span> and 
            <span className="text-jamaica-yellow font-semibold"> island treasures</span> organised by category
          </p>
          <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
            Discover curated collections of authentic Jamaican products organised by category.
          </p>
        </div>

        {/* Full-bleed row that matches viewport width */}
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Left/Right gradient fades (desktop only) */}
            <div className="hidden md:block pointer-events-none absolute left-0 top-0 h-full w-10 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="hidden md:block pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent z-10" />


            {/* Scroll arrows (desktop only) */}
            <div className="hidden md:flex items-center justify-between absolute inset-y-0 left-0 right-0 z-20 px-1 pointer-events-none">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Scroll left"
                disabled={!canScrollLeft}
                onClick={() => scrollByAmount("left")}
                className="rounded-full bg-white/70 hover:bg-white shadow-lg border border-white/60 backdrop-blur-sm pointer-events-auto"
              >
                <ChevronLeft className="h-5 w-5 text-gray-700" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Scroll right"
                disabled={!canScrollRight}
                onClick={() => scrollByAmount("right")}
                className="rounded-full bg-white/70 hover:bg-white shadow-lg border border-white/60 backdrop-blur-sm pointer-events-auto"
              >
                <ChevronRight className="h-5 w-5 text-gray-700" />
              </Button>
            </div>

            {/* Horizontal scroller */}
            <div ref={scrollerRef} className="flex gap-4 overflow-x-auto no-scrollbar py-2 scroll-smooth">
            {categories.map((cat) => {
              const title = toTitleCase(cat.name);
              return (
                <button
                  key={cat.id}
                  className="relative flex-shrink-0 w-44 h-28 sm:w-56 sm:h-36 rounded-xl overflow-hidden border border-gray-100 transition-all duration-300 text-left bg-white/90 backdrop-blur-sm"
                  onClick={() => navigate(`/products?cat=${encodeURIComponent(cat.name)}`)}
                  aria-label={`View ${title}`}
                >
                  <Card className="w-full h-full rounded-xl overflow-hidden border-0 bg-transparent">
                    <CardContent className="p-0 w-full h-full">
                      <div
                        className="w-full h-full bg-cover bg-center"
                        style={{
                          backgroundImage: cat.image
                            ? `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${cat.image})`
                            : undefined,
                          backgroundColor: cat.image ? undefined : "#f3f4f6",
                        }}
                      >
                        <div className="absolute inset-0" />
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <div className="text-white text-sm sm:text-base font-semibold drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
                            {title}
                          </div>
                          <div className="text-white/80 text-[11px] sm:text-xs">
                            {cat.count} {cat.count === 1 ? "item" : "items"}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              );
            })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopByCategory;

